import { useCallback, useMemo } from 'react';
import { useIoContext } from "../../../../contexts";
import { useServiceBillingDetails } from "../../../../hooks/reactQuery/queries/useServiceBillingDetails";
import { isAfterOrEqual, format, startOfDay, isBefore } from "../../../../infra/utils/dates-fns";
import { serviceModel } from "../../../../models";
import { pluralize } from "../../../../utils/pluralize";
import { StartAutomaticBillingAtControlDropdownMenuItem } from "../StartAutomaticBillingAtControl";
export const useCallOuts = _ref => {
  let {
    testIds,
    relationshipEffectiveDate,
    service,
    selectedItem,
    isChecked,
    isAmendedService
  } = _ref;
  const {
    StartAutomaticBillingAtControlTestIds
  } = testIds;
  const {
    getNow
  } = useIoContext();
  const getToday = useCallback(() => startOfDay(getNow()), [getNow]);
  const isProrated = serviceModel.isProrated(service);
  const isBilledUpfront = serviceModel.isBilledUpfront(service);
  const {
    nextBillingDate,
    periods
  } = useServiceBillingDetails({
    service,
    relationshipEffectiveDate: relationshipEffectiveDate
  });
  const startBillingAt = serviceModel.startBillingAt(service);
  const shouldShowProrateWarning = useMemo(() => {
    if (!isProrated && isBilledUpfront && startBillingAt && nextBillingDate) {
      return nextBillingDate.getTime() !== startBillingAt.getTime();
    }
    return false;
  }, [nextBillingDate, startBillingAt, isProrated, isBilledUpfront]);
  return useMemo(() => {
    const _callouts = [];
    if (!isChecked) {
      return _callouts;
    }
    if (selectedItem === StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate && startBillingAt) {
      if (!isAmendedService && isAfterOrEqual(startBillingAt, getToday())) {
        if (relationshipEffectiveDate) {
          _callouts.push({
            dataTestid: StartAutomaticBillingAtControlTestIds.Callouts.FutureDate(),
            text: "If the proposal is signed after ".concat(format(startBillingAt, 'MMM d, yyyy'), ", any charges for prior periods will be invoiced upon approval.")
          });
        } else {
          _callouts.push({
            dataTestid: StartAutomaticBillingAtControlTestIds.Callouts.OnAcceptanceDate(),
            text: "Since the agreement\u2019s effective date is set to \u201COn acceptance,\u201D signing it after ".concat(format(startBillingAt, 'MMM d, yyyy'), " will start billing in the next cycle and exclude prior charges.")
          });
        }
      }
      if (shouldShowProrateWarning) {
        _callouts.push({
          dataTestid: StartAutomaticBillingAtControlTestIds.Callouts.Proration(),
          text: 'As the selected date doesn’t match the billing day, an additional full invoice will be issued. You can adjust the date or use “first month proration” to align payments.'
        });
      }
      if (periods && periods > 0 && isBefore(startBillingAt, getToday())) {
        _callouts.push({
          dataTestid: StartAutomaticBillingAtControlTestIds.Callouts.PriorPeriods(),
          text: "Invoices for ".concat(periods, " prior ").concat(pluralize('periods', periods), " will be issued once the proposal is approved. If the proposal is signed after ").concat(format(startBillingAt, 'MMM d, yyyy'), ", additional invoices for earlier periods will also be issued.")
        });
      }
    }
    return _callouts;
  }, [selectedItem, isChecked, startBillingAt, shouldShowProrateWarning, periods, relationshipEffectiveDate, isAmendedService, getToday]);
};